import React from 'react';

interface TechAmbientBackgroundProps {
  className?: string;
}

/**
 * TechAmbientBackground
 * 
 * Capa de ambientación tecnológica minimalista con hardware emblemático de BARTECH:
 * - CPU Core
 * - Capturador de datos (PDA industrial sin antenas)
 * - Scanner de código de barras 2D
 * - Balanza Digital POS Comercial
 * - Lector Omnidireccional 2D
 * - Contador de Billetes & Banca
 * - Impresora térmica POS
 * - Terminal All-in-One Touch POS
 * 
 * Renderizado estático y limpio en los iconos, con pulsos de datos fluidos
 * exclusivamente sobre las pistas del circuito PCB.
 */
export const TechAmbientBackground: React.FC<TechAmbientBackgroundProps> = ({
  className = 'absolute inset-0 pointer-events-none overflow-hidden select-none -z-10',
}) => {
  return (
    <div className={className} aria-hidden="true">
      {/* 1. Gradientes Radiales Ambientales */}
      <div
        className="absolute -top-24 -left-24 w-[550px] h-[550px] rounded-full ambient-glow-1 opacity-70 dark:opacity-85"
      />
      <div
        className="absolute top-[20%] left-[50%] -translate-x-1/2 w-[520px] h-[520px] rounded-full ambient-glow-center opacity-65 dark:opacity-80"
      />
      <div
        className="absolute top-[45%] -right-28 w-[600px] h-[600px] rounded-full ambient-glow-2 opacity-65 dark:opacity-80"
      />
      <div
        className="absolute -bottom-28 left-[15%] w-[550px] h-[550px] rounded-full ambient-glow-3 opacity-60 dark:opacity-75"
      />

      {/* 2. Retícula Técnica Blueprint Micro-Dot */}
      <svg
        className="absolute inset-0 w-full h-full opacity-55 dark:opacity-75"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="bartech-tech-grid"
            width="44"
            height="44"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="22" cy="22" r="0.9" fill="#33CC00" className="opacity-45 dark:opacity-65" />
            <circle cx="0" cy="0" r="0.6" fill="#33CC00" className="opacity-25 dark:opacity-40" />
            <circle cx="44" cy="0" r="0.6" fill="#33CC00" className="opacity-25 dark:opacity-40" />
            <circle cx="0" cy="44" r="0.6" fill="#33CC00" className="opacity-25 dark:opacity-40" />
            <circle cx="44" cy="44" r="0.6" fill="#33CC00" className="opacity-25 dark:opacity-40" />
          </pattern>

          <radialGradient id="grid-fade-mask" cx="50%" cy="45%" r="75%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.95" />
            <stop offset="65%" stopColor="#fff" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0.15" />
          </radialGradient>
          <mask id="fade-mask">
            <rect width="100%" height="100%" fill="url(#grid-fade-mask)" />
          </mask>
        </defs>

        <rect
          width="100%"
          height="100%"
          fill="url(#bartech-tech-grid)"
          mask="url(#fade-mask)"
        />
      </svg>

      {/* 3. Iconos de Hardware POS & Trazas de Conectividad (SVG optimizado en Desktop) */}
      <svg
        className="hidden md:block absolute inset-0 w-full h-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="trace-grad-hardware-1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#33CC00" stopOpacity="0.10" />
            <stop offset="30%" stopColor="#33CC00" stopOpacity="0.50" />
            <stop offset="70%" stopColor="#33CC00" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#33CC00" stopOpacity="0.10" />
          </linearGradient>

          <linearGradient id="trace-grad-hardware-center" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#33CC00" stopOpacity="0.10" />
            <stop offset="25%" stopColor="#33CC00" stopOpacity="0.55" />
            <stop offset="75%" stopColor="#33CC00" stopOpacity="0.50" />
            <stop offset="100%" stopColor="#33CC00" stopOpacity="0.10" />
          </linearGradient>
        </defs>

        {/* ========================================================================= */}
        {/* 1. NODO CENTRAL: PROCESADOR / CPU CORE                                    */}
        {/* ========================================================================= */}
        <g className="opacity-80 dark:opacity-95">
          <path
            d="M 680 0 L 680 150 L 720 190 L 720 280 M 720 340 L 720 460 L 760 500 L 760 900"
            fill="none"
            stroke="url(#trace-grad-hardware-center)"
            strokeWidth="1.5"
          />

          {/* ICONO: CPU */}
          <g transform="translate(695, 280)">
            <rect
              x="0"
              y="0"
              width="50"
              height="50"
              rx="6"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1.5"
              className="opacity-70 dark:opacity-90"
            />
            <rect
              x="13"
              y="13"
              width="24"
              height="24"
              rx="3"
              fill="#33CC00"
              className="opacity-20 dark:opacity-35"
            />
            <rect
              x="18"
              y="18"
              width="14"
              height="14"
              rx="2"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1"
              className="opacity-60"
            />
            {/* Pines Superiores e Inferiores */}
            <line x1="12" y1="0" x2="12" y2="-6" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="25" y1="0" x2="25" y2="-6" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="38" y1="0" x2="38" y2="-6" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="12" y1="50" x2="12" y2="56" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="25" y1="50" x2="25" y2="56" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="38" y1="50" x2="38" y2="56" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            {/* Pines Laterales */}
            <line x1="0" y1="12" x2="-6" y2="12" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="0" y1="25" x2="-6" y2="25" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="0" y1="38" x2="-6" y2="38" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="50" y1="12" x2="56" y2="12" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="50" y1="25" x2="56" y2="25" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <line x1="50" y1="38" x2="56" y2="38" stroke="#33CC00" strokeWidth="1.2" className="opacity-70" />
            <circle cx="25" cy="25" r="2" fill="#33CC00" className="opacity-75" />
            <text
              x="25"
              y="68"
              textAnchor="middle"
              fill="#33CC00"
              fontSize="7.5"
              fontWeight="bold"
              letterSpacing="1"
              className="opacity-70 dark:opacity-85"
            >
              CPU CORE
            </text>
          </g>

          <circle cx="680" cy="150" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-55" />
          <circle cx="720" cy="190" r="2" fill="#33CC00" className="opacity-75" />
          <circle cx="760" cy="500" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-55" />

          {/* Pulso de Datos Central */}
          <path
            d="M 680 0 L 680 150 L 720 190 L 720 280 M 720 340 L 720 460 L 760 500 L 760 900"
            fill="none"
            stroke="#33CC00"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="circuit-pulse-center-1"
          />
        </g>

        {/* ========================================================================= */}
        {/* 2. NODO SUPERIOR DERECHO: CAPTURADOR DE DATOS (PDA INDUSTRIAL LIMPIO)     */}
        {/* ========================================================================= */}
        <g className="opacity-75 dark:opacity-90">
          <path
            d="M 1258 0 L 1258 40 M 1258 114 L 1258 145 L 1315 195 L 1440 195"
            fill="none"
            stroke="url(#trace-grad-hardware-1)"
            strokeWidth="1.4"
          />

          {/* ICONO: CAPTURADOR PDA LIMPIO (Sin antenas ni parpadeos) */}
          <g transform="translate(1240, 40)">
            <rect
              x="0"
              y="0"
              width="36"
              height="62"
              rx="6"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1.5"
              className="opacity-70 dark:opacity-90"
            />
            <rect
              x="4.5"
              y="10"
              width="27"
              height="36"
              rx="2"
              fill="#33CC00"
              className="opacity-15 dark:opacity-25"
            />
            <rect
              x="11"
              y="4"
              width="14"
              height="2.5"
              rx="1"
              fill="#33CC00"
              className="opacity-60"
            />
            {/* Botones de navegación inferiores limpios y estáticos */}
            <circle cx="11" cy="53" r="1.3" fill="#33CC00" className="opacity-60" />
            <circle cx="18" cy="53" r="1.5" fill="#33CC00" className="opacity-75" />
            <circle cx="25" cy="53" r="1.3" fill="#33CC00" className="opacity-60" />
            <text
              x="18"
              y="74"
              textAnchor="middle"
              fill="#33CC00"
              fontSize="7"
              fontWeight="bold"
              letterSpacing="0.8"
              className="opacity-70 dark:opacity-85"
            >
              CAPTURADOR
            </text>
          </g>

          <circle cx="1258" cy="20" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-55" />
          <circle cx="1315" cy="195" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-55" />

          <path
            d="M 1258 0 L 1258 40 M 1258 114 L 1258 145 L 1315 195 L 1440 195"
            fill="none"
            stroke="#33CC00"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="circuit-pulse-top-right"
          />
        </g>

        {/* ========================================================================= */}
        {/* 3. NODO LATERAL IZQUIERDO: SCANNER 2D (LIMPIO)                            */}
        {/* ========================================================================= */}
        <g className="opacity-75 dark:opacity-90">
          <path
            d="M 0 320 L 100 320 L 150 370 L 180 370 M 240 370 L 290 420 L 360 420"
            fill="none"
            stroke="url(#trace-grad-hardware-1)"
            strokeWidth="1.4"
          />

          {/* ICONO: SCANNER 2D */}
          <g transform="translate(185, 345)">
            <path
              d="M 6 0 L 40 0 C 44 0 47 3 47 7 L 47 22 C 47 26 44 29 40 29 L 26 29 L 20 48 C 19 51 16 53 13 53 L 7 53 C 4 53 2 50 3 47 L 8 28 L 6 28 C 3 28 0 25 0 22 L 0 6 C 0 3 3 0 6 0 Z"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1.4"
              className="opacity-70 dark:opacity-90"
            />
            <line x1="3" y1="4" x2="3" y2="24" stroke="#33CC00" strokeWidth="2" className="opacity-75" />
            <circle cx="16" cy="33" r="1.5" fill="#33CC00" className="opacity-50" />
            <circle cx="34" cy="14" r="2" fill="#33CC00" className="opacity-75" />
            <text
              x="24"
              y="65"
              textAnchor="middle"
              fill="#33CC00"
              fontSize="7"
              fontWeight="bold"
              letterSpacing="0.8"
              className="opacity-70 dark:opacity-85"
            >
              SCANNER 2D
            </text>
          </g>

          <circle cx="100" cy="320" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />
          <circle cx="150" cy="370" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />

          <path
            d="M 0 320 L 100 320 L 150 370 L 180 370 M 240 370 L 290 420 L 360 420"
            fill="none"
            stroke="#33CC00"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="circuit-pulse-mid-left"
          />
        </g>

        {/* ========================================================================= */}
        {/* 4. RED DE CIRCUITOS LATERAL DERECHO MEDIO                                */}
        {/* ========================================================================= */}
        <g className="opacity-75 dark:opacity-90">
          <path
            d="M 1440 370 L 1340 370 L 1280 430 L 1280 530 L 1330 580 L 1330 625 L 1440 625 M 1280 530 L 1220 590 L 1150 590"
            fill="none"
            stroke="url(#trace-grad-hardware-1)"
            strokeWidth="1.4"
          />

          {/* Nodos de interconexión PCB */}
          <circle cx="1340" cy="370" r="3.2" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-55" />
          <circle cx="1340" cy="370" r="1.3" fill="#33CC00" className="opacity-75" />
          <circle cx="1280" cy="430" r="2.8" fill="#33CC00" className="opacity-75" />
          <circle cx="1280" cy="530" r="3.2" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-55" />
          <circle cx="1280" cy="530" r="1.3" fill="#33CC00" className="opacity-75" />
          <circle cx="1330" cy="580" r="2.5" fill="#33CC00" className="opacity-70" />
          <circle cx="1330" cy="625" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />

          <path
            d="M 1440 370 L 1340 370 L 1280 430 L 1280 530 L 1330 580 L 1330 625 L 1440 625 M 1280 530 L 1220 590 L 1150 590"
            fill="none"
            stroke="#33CC00"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="circuit-pulse-mid-right"
          />
        </g>

        {/* ========================================================================= */}
        {/* 5. NODO CENTRO-DERECHO: CONTADOR DE BILLETES & BANCA (LIMPIO)              */}
        {/* ========================================================================= */}
        <g className="opacity-70 dark:opacity-85">
          <path
            d="M 760 500 L 850 500 L 900 550 L 960 550 M 1010 575 L 1050 575 L 1090 615 L 1090 660 L 1150 660 L 1190 700"
            fill="none"
            stroke="url(#trace-grad-hardware-1)"
            strokeWidth="1.4"
          />

          {/* ICONO: CONTADOR DE BILLETES */}
          <g transform="translate(960, 560)">
            <rect
              x="0"
              y="8"
              width="50"
              height="38"
              rx="5"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1.4"
              className="opacity-70 dark:opacity-90"
            />
            {/* Tolva de alimentación (Hopper) */}
            <path d="M 6 8 L 6 1 L 44 1 L 44 8" fill="none" stroke="#33CC00" strokeWidth="1.2" className="opacity-60" />
            {/* Pantalla digital limpia */}
            <rect x="7" y="14" width="22" height="10" rx="2" fill="#33CC00" className="opacity-20 dark:opacity-30" />
            {/* Ranura de salida */}
            <rect x="7" y="29" width="36" height="11" rx="2" fill="none" stroke="#33CC00" strokeWidth="1.1" className="opacity-65" />
            <circle cx="39" cy="19" r="1.5" fill="#33CC00" className="opacity-75" />

            <text
              x="25"
              y="58"
              textAnchor="middle"
              fill="#33CC00"
              fontSize="7"
              fontWeight="bold"
              letterSpacing="0.8"
              className="opacity-70 dark:opacity-85"
            >
              CONTADOR BILLETES
            </text>
          </g>

          <circle cx="850" cy="500" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />
          <circle cx="1050" cy="575" r="2.8" fill="#33CC00" className="opacity-70" />
          <circle cx="1090" cy="660" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />

          <path
            d="M 760 500 L 850 500 L 900 550 L 960 550 M 1010 575 L 1050 575 L 1090 615 L 1090 660 L 1150 660 L 1190 700"
            fill="none"
            stroke="#33CC00"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="circuit-pulse-mid-branch"
          />
        </g>

        {/* ========================================================================= */}
        {/* 6. NODO CENTRO-IZQUIERDO: LECTOR OMNIDIRECCIONAL 2D (LIMPIO)               */}
        {/* ========================================================================= */}
        <g className="opacity-70 dark:opacity-85">
          <path
            d="M 360 420 L 410 470 L 410 520 M 445 570 L 445 620 L 380 685 L 300 685"
            fill="none"
            stroke="url(#trace-grad-hardware-1)"
            strokeWidth="1.4"
          />

          {/* ICONO: LECTOR OMNIDIRECCIONAL 2D */}
          <g transform="translate(420, 520)">
            <rect x="8" y="38" width="34" height="8" rx="2" fill="none" stroke="#33CC00" strokeWidth="1.3" className="opacity-70 dark:opacity-90" />
            <line x1="25" y1="38" x2="25" y2="30" stroke="#33CC00" strokeWidth="2" className="opacity-75" />
            <rect x="4" y="2" width="42" height="28" rx="10" fill="none" stroke="#33CC00" strokeWidth="1.4" className="opacity-70 dark:opacity-90" />
            <circle cx="25" cy="16" r="10" fill="#33CC00" className="opacity-15 dark:opacity-25" />
            <circle cx="25" cy="16" r="10" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-60" />
            <line x1="17" y1="16" x2="33" y2="16" stroke="#33CC00" strokeWidth="1" className="opacity-70" />
            <line x1="25" y1="8" x2="25" y2="24" stroke="#33CC00" strokeWidth="1" className="opacity-70" />
            <circle cx="25" cy="4" r="1.3" fill="#33CC00" className="opacity-80" />

            <text
              x="25"
              y="58"
              textAnchor="middle"
              fill="#33CC00"
              fontSize="7"
              fontWeight="bold"
              letterSpacing="0.8"
              className="opacity-70 dark:opacity-85"
            >
              LECTOR OMNI 2D
            </text>
          </g>

          <circle cx="410" cy="470" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />
          <circle cx="380" cy="685" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />

          <path
            d="M 360 420 L 410 470 L 410 520 M 445 570 L 445 620 L 380 685 L 300 685"
            fill="none"
            stroke="#33CC00"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="circuit-pulse-mid-left"
          />
        </g>

        {/* ========================================================================= */}
        {/* 7. NODO INFERIOR IZQUIERDO: IMPRESORA TÉRMICA POS (LIMPIA)                 */}
        {/* ========================================================================= */}
        <g className="opacity-75 dark:opacity-90">
          <path
            d="M 0 740 L 70 740 L 120 790 L 170 790 M 230 790 L 280 840 L 340 840"
            fill="none"
            stroke="url(#trace-grad-hardware-1)"
            strokeWidth="1.4"
          />

          {/* ICONO: IMPRESORA POS */}
          <g transform="translate(175, 760)">
            <rect
              x="0"
              y="10"
              width="48"
              height="38"
              rx="5"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1.4"
              className="opacity-70 dark:opacity-90"
            />
            <rect
              x="8"
              y="16"
              width="32"
              height="4"
              rx="1"
              fill="#33CC00"
              className="opacity-40"
            />
            <path
              d="M 10 13 L 10 2 L 36 2 L 36 13"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1.2"
              className="opacity-60"
            />
            <circle cx="12" cy="38" r="2" fill="none" stroke="#33CC00" strokeWidth="0.8" className="opacity-60" />
            <circle cx="36" cy="38" r="2" fill="#33CC00" className="opacity-75" />
            <text
              x="24"
              y="59"
              textAnchor="middle"
              fill="#33CC00"
              fontSize="7"
              fontWeight="bold"
              letterSpacing="0.8"
              className="opacity-70 dark:opacity-85"
            >
              IMPRESORA POS
            </text>
          </g>

          <circle cx="70" cy="740" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />
          <circle cx="120" cy="790" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />

          <path
            d="M 0 740 L 70 740 L 120 790 L 170 790 M 230 790 L 280 840 L 340 840"
            fill="none"
            stroke="#33CC00"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="circuit-pulse-bottom-left"
          />
        </g>

        {/* ========================================================================= */}
        {/* 8. NODO INFERIOR DERECHO: TERMINAL ALL-IN-ONE TOUCH POS (LIMPIA)          */}
        {/* ========================================================================= */}
        <g className="opacity-75 dark:opacity-90">
          <path
            d="M 940 900 L 990 850 L 1150 850 L 1190 810 M 1260 810 L 1340 810 L 1400 870"
            fill="none"
            stroke="url(#trace-grad-hardware-1)"
            strokeWidth="1.4"
          />

          {/* ICONO: TERMINAL TOUCH ALL-IN-ONE POS */}
          <g transform="translate(1195, 770)">
            <rect
              x="0"
              y="0"
              width="54"
              height="38"
              rx="4"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1.5"
              className="opacity-70 dark:opacity-90"
            />
            <rect
              x="4"
              y="4"
              width="46"
              height="30"
              rx="2"
              fill="#33CC00"
              className="opacity-15 dark:opacity-25"
            />
            <path
              d="M 27 38 L 27 50 L 15 54 L 39 54 L 27 50"
              fill="none"
              stroke="#33CC00"
              strokeWidth="1.3"
              className="opacity-60"
            />
            <circle cx="48" cy="33" r="1.5" fill="#33CC00" className="opacity-75" />
            <text
              x="27"
              y="66"
              textAnchor="middle"
              fill="#33CC00"
              fontSize="7"
              fontWeight="bold"
              letterSpacing="0.8"
              className="opacity-70 dark:opacity-85"
            >
              TERMINAL POS
            </text>
          </g>

          <circle cx="990" cy="850" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />
          <circle cx="1150" cy="850" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />
          <circle cx="1340" cy="810" r="3" fill="none" stroke="#33CC00" strokeWidth="1" className="opacity-50" />

          <path
            d="M 940 900 L 990 850 L 1150 850 L 1190 810 M 1260 810 L 1340 810 L 1400 870"
            fill="none"
            stroke="#33CC00"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="circuit-pulse-bottom-right"
          />
        </g>
      </svg>
    </div>
  );
};
